import * as THREE from 'three';
import { simpleNoise } from './utils/noise.js';

/**
 * Klasa zarządzająca drogą z dynamicznym generowaniem segmentów
 * Wersja z InstancedMesh - wszystkie segmenty linii w jednym draw callu.
 *
 * - Linie renderowane jako jeden InstancedMesh cylindrów jednostkowych (1 draw call)
 * - Materiał MeshBasicMaterial (brak PBR) + bloom - tańszy fragment shader
 * - Zaawansowana obsługa załamań (poziome i pionowe)
 * - Rebuild na żądanie (flaga _dirty) przy dodaniu/usunięciu segmentu
 *
 * Macierz punktów w przestrzeni świata, kamera się przesuwa
 */
export class Road {
    constructor(config = {}) {
        this.config = {
            linesCount: config.linesCount || 10, // Zwiększona gęstość pionowych linii (było 10, teraz 12)
            startZ: config.horizonZ || config.startZ || -50, // Przesunięte do przodu (bliżej kamery) - było -200
            roadWidth: config.roadWidthAtBottom || config.roadWidth || 100, // Szerokość drogi
            segmentSpacing: config.segmentSpacing || 20, // Odstęp między segmentami
            cameraSpeed: config.cameraSpeed || 1.0,
            verticalRandomness: config.verticalRandomness || 0.5, // Amplituda losowości w pionie
            blockNewSegments: config.blockNewSegments || false, // Blokada dodawania nowych segmentów
            lineWidth: config.lineWidth || 1.0, // Grubość linii (używane jako promień cylindra)
            glowIntensity: config.glowIntensity || 1.0, // Intensywność efektu glow
            useThickLines: config.useThickLines !== undefined ? config.useThickLines : true, // Użyj cylindrów zamiast linii
            lineBreaks: config.lineBreaks || {
                horizontal: false,
                vertical: false,
                position: 'random', // 'random' | 'even' - even = równomiernie dzieli linię
                strength: 1.0,
                count: 2,
                skipVerticalForBreaks: false // Nie łącz pionowymi liniami punktów załamań
            }
        };
        
        this.group = new THREE.Group();
        
        this.lastAddedSegmentZ = null; // Ostatnio dodany Z segmentu (aby uniknąć wielokrotnego dodawania)
        this.maxRows = config.maxRows || 30; // Maksymalna liczba rzędów siatki

        // Pozycja Y płaszczyzny maski = wysokość bazowa linii (-5).
        // Maska musi być na poziomie siatki, żeby blokować słońce za horyzontem.
        // Linie mają depthTest:false → renderują się zawsze NA WIERZCHU maski, więc nie są ucinane.
        this.baseLineY = -5;
        this.planeY = this.baseLineY;

        // Renderowanie linii: pojedynczy InstancedMesh cylindrów (grube) lub LineSegments (cienkie).
        // Rebuild na żądanie (flaga _dirty) → 1 draw call zamiast setek osobnych meshy.
        this.lineMesh = null;
        this.lineMeshCapacity = 0; // pojemność InstancedMesh (prealokacja, unika dispose/recreate)
        this.unitCylinderGeometry = null;
        this.cylinderRadialSegments = 0; // aktualna liczba segmentów obwodu geometrii
        this._dirty = false;

        // Kolor bazowy - jasny turkusowy
        this.baseColor = 0x15F8E5;
        const glowIntensity = this.config.glowIntensity || 1.0;
        const baseOpacity = 0.8;

        // Boost koloru powyżej 1.0 (HDR) - klucz do silnego bloom na liniach.
        // EffectComposer używa render targetu HalfFloat, więc wartości >1 trafiają do bloom.
        // Słońce ma emissiveIntensity ~1.5, więc boost linii > 1.5 daje silniejszy bloom niż słońce.
        this.bloomBoost = this.computeBloomBoost(glowIntensity);

        // Materiał linii (cylindry InstancedMesh).
        // MeshBasicMaterial jest znacznie tańszy od MeshStandardMaterial (brak PBR / światła).
        // toneMapped:false + color > 1 → wartość HDR trafia bezpośrednio do bloom.
        // depthTest:false + depthWrite:false → linie zawsze NA WIERZCHU maski (nie ucinane),
        // renderOrder=1 i tak rysuje je po masce/słońcu.
        this.material = new THREE.MeshBasicMaterial({
            color: this.baseColor,
            transparent: true,
            opacity: Math.min(baseOpacity * glowIntensity, 1.0),
            depthTest: false,
            depthWrite: false,
            toneMapped: false
        });
        this.material.color.multiplyScalar(this.bloomBoost);

        // Materiał dla fallbacku cienkich linii (LineSegments)
        this.lineMaterial = new THREE.LineBasicMaterial({
            color: this.baseColor,
            transparent: true,
            opacity: Math.min(baseOpacity * glowIntensity, 1.0),
            depthTest: false,
            depthWrite: false,
            toneMapped: false
        });
        this.lineMaterial.color.multiplyScalar(this.bloomBoost);
        
        // Macierz punktów w przestrzeni świata
        // [row][col] = {x, y, z} gdzie z to pozycja w świecie
        this.gridPoints = [];
    }
    
    /**
     * Inicjalizacja początkowej siatki
     */
    init() {
        this.generateInitialGrid();
        this.updateGroupPosition();
        
        // Ustaw renderOrder dla całej grupy, żeby była renderowana po słońcu
        this.group.renderOrder = 1;
        
        return this.group;
    }
    
    /**
     * Aktualizacja pozycji grupy - droga w pozycji 0,0,0
     */
    updateGroupPosition() {
        // Grupa w pozycji 0,0,0 - punkty są w przestrzeni świata
        this.group.position.set(0, 0, 0);
    }
    
    /**
     * Generowanie początkowej siatki punktów
     * Punkty równomiernie rozłożone w przestrzeni 3D
     */
    generateInitialGrid() {
        const gridSize = 20; // Początkowa liczba rzędów
        const segmentSpacing = this.config.segmentSpacing;
        const linesCount = this.config.linesCount;
        const roadWidth = this.config.roadWidth;
        const startZ = this.config.startZ;
        
        // Generuj macierz punktów równomiernie w przestrzeni 3D
        for (let row = 0; row < gridSize; row++) {
            this.gridPoints[row] = [];
            
            // Pozycja Z w przestrzeni świata
            const z = startZ + (row * segmentSpacing);
            
            // Stała szerokość dla wszystkich rzędów
            const width = roadWidth;
            
            for (let col = 0; col <= linesCount; col++) {
                // Rozkład punktów w linii poziomej - równomiernie
                const u = col / linesCount; // 0 = lewy, 1 = prawy
                const x = (u - 0.5) * 2 * width; // Rozkład od -width do +width
                
                // Y bez distortion - distortion będzie dodany po załamaniach
                const y = -5;
                
                this.gridPoints[row][col] = { x, y, z };
            }
        }
        
        // Dodaj distortion do podstawowych punktów przed załamaniami
        this.addDistortionToPoints();
        
        // Dodaj załamania do punktów (punkty załamań będą miały Y z interpolacji między punktami z distortion)
        this.addBreaksToGridPoints();
        
        // NIE dodajemy distortion do punktów załamań - one już mają właściwe Y z interpolacji!
        
        // Najpierw dodaj płaszczyznę (będzie pod liniami)
        this.createRoadPlane();
        
        // Potem utworzenie linii z punktów (będą na wierzchu) - jeden InstancedMesh
        this.rebuildLines();
    }
    
    /**
     * Utworzenie płaszczyzny pod liniami drogi, żeby zasłaniać słońce
     */
    createRoadPlane() {
        // Oblicz rozmiar płaszczyzny na podstawie punktów siatki
        const rows = this.gridPoints.length;
        if (rows === 0) return;
        
        const firstRow = this.gridPoints[0];
        const lastRow = this.gridPoints[rows - 1];
        if (!firstRow || !lastRow) return;
        
        // Znajdź min/max x i z
        let minX = Infinity, maxX = -Infinity;
        let minZ = Infinity, maxZ = -Infinity;
        
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < this.gridPoints[row].length; col++) {
                const point = this.gridPoints[row][col];
                if (point) {
                    minX = Math.min(minX, point.x);
                    maxX = Math.max(maxX, point.x);
                    minZ = Math.min(minZ, point.z);
                    maxZ = Math.max(maxZ, point.z);
                }
            }
        }
        
        // Zmniejsz rozmiar płaszczyzny, żeby nie przysłaniała linii
        const margin = 0.5; // Margines w jednostkach - zmniejsza płaszczyznę
        const width = (maxX - minX) - margin * 2;
        const depth = (maxZ - minZ) - margin * 2;
        const centerX = (minX + maxX) / 2;
        const centerZ = (minZ + maxZ) / 2;
        
        // Utworzenie płaszczyzny - czarna, NIEPRZEZROCZYSTA, żeby blokować słońce za horyzontem.
        // Geometria 1x1 skalowana w runtime (updateRoadPlane) - brak realokacji.
        const planeGeometry = new THREE.PlaneGeometry(1, 1, 1, 1);
        const planeMaterial = new THREE.MeshBasicMaterial({
            color: 0x000000,
            transparent: false, // Nieprzezroczysta - słońce nie przebija się przez siatkę
            side: THREE.FrontSide, // Widoczna tylko z góry - tańsze niż DoubleSide
            depthWrite: true,
            depthTest: true,
            toneMapped: false
        });
        
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        plane.position.set(centerX, this.planeY, centerZ); // Poniżej zakresu distortionu linii
        plane.scale.set(Math.max(width, 0.1), Math.max(depth, 0.1), 1);
        plane.rotation.x = -Math.PI / 2; // Obróć poziomo
        plane.renderOrder = 0; // Renderuj przed liniami (niższy = wcześniej, więc będzie pod liniami)
        
        // Dodaj płaszczyznę na początku grupy, żeby była pod liniami
        this.group.add(plane);
        this.roadPlane = plane; // Zapisz referencję dla późniejszej aktualizacji
    }
    
    /**
     * Aktualizacja płaszczyzny drogi po dodaniu nowych segmentów.
     * O(1): x jest stałe per kolumna (roadWidth stałe), z jest stałe per rząd.
     * minX/maxX zależą tylko od roadWidth, minZ/maxZ = Z pierwszego/ostatniego rzędu.
     */
    updateRoadPlane() {
        if (!this.roadPlane || !this.gridPoints || this.gridPoints.length === 0) {
            return;
        }

        const firstRow = this.gridPoints[0];
        const lastRow = this.gridPoints[this.gridPoints.length - 1];
        if (!firstRow?.length || !lastRow?.length) return;

        const width = this.config.roadWidth;
        const minZ = firstRow[0].z;
        const maxZ = lastRow[0].z;
        const margin = 0.5;

        const planeWidth = (width * 2) - margin * 2;
        const depth = (maxZ - minZ) - margin * 2;
        const centerZ = (minZ + maxZ) / 2;

        // Aktualizuj tylko scale i pozycję - bez dispose/recreate geometrii.
        this.roadPlane.scale.set(Math.max(planeWidth, 0.1), Math.max(depth, 0.1), 1);
        this.roadPlane.position.set(0, this.planeY, centerZ);
    }
    
    /**
     * Dodanie distortion tylko do nowego segmentu (pierwszy rząd - podstawowe punkty)
     */
    addDistortionToNewSegment() {
        if (this.config.verticalRandomness === 0) {
            return; // Brak distortion
        }
        
        if (this.gridPoints.length < 1) return;
        
        const firstRow = this.gridPoints[0];
        if (!firstRow || firstRow.length === 0) return;
        
        const baseCols = this.config.linesCount + 1;
        
        // Dodaj distortion tylko do podstawowych punktów pierwszego rzędu
        // Używamy flagi _hasDistortion aby uniknąć wielokrotnego dodawania
        for (let col = 0; col < Math.min(firstRow.length, baseCols); col++) {
            const point = firstRow[col];
            if (point && point.x !== undefined && !point._hasDistortion) {
                // Dodaj distortion tylko jeśli punkt nie ma jeszcze distortion
                const noise = simpleNoise(point.x * 0.1, point.z * 0.1);
                const randomOffset = (noise - 0.5) * 2 * this.config.verticalRandomness;
                point.y = point.y + randomOffset;
                point._hasDistortion = true; // Oznacz że punkt ma już distortion
            }
        }
    }
    
    /**
     * Dodanie załamań tylko do nowego segmentu (pierwszy rząd)
     * Używane przy dodawaniu nowych segmentów - sprawdza czy siatka ma już załamania
     * i jeśli tak, dodaje załamania do nowego segmentu w taki sam sposób
     */
    addBreaksToNewSegment() {
        const breaksConfig = this.config.lineBreaks;
        if (!breaksConfig || (!breaksConfig.horizontal && !breaksConfig.vertical)) {
            return; // Brak załamań
        }
        
        if (this.gridPoints.length < 1) return;
        
        const firstRow = this.gridPoints[0];
        if (!firstRow || firstRow.length === 0) return;
        
        const baseCols = this.config.linesCount + 1;
        
        // Sprawdź czy siatka już ma załamania (sprawdź drugi rząd jeśli istnieje)
        const hasBreaks = this.gridPoints.length > 1 && 
                         this.gridPoints[1] && 
                         this.gridPoints[1].length > baseCols;
        
        // Jeśli siatka ma załamania, dodaj je też do nowego segmentu
        if (hasBreaks && breaksConfig.horizontal && breaksConfig.count > 0) {
            // Nowy segment ma podstawową liczbę kolumn, rozszerz go o załamania
            if (firstRow.length === baseCols) {
                const newRow = [];
                for (let col = 0; col < firstRow.length; col++) {
                    // Kopiuj punkt (nie referencję) aby uniknąć problemów z distortion
                    const point = firstRow[col];
                    newRow.push({ x: point.x, y: point.y, z: point.z, _hasDistortion: point._hasDistortion });
                    
                    // Jeśli to nie ostatnia kolumna, dodaj załamania
                    if (col < firstRow.length - 1) {
                        const breaks = this.generateBreaksBetweenPoints(
                            firstRow[col],
                            firstRow[col + 1],
                            breaksConfig,
                            'horizontal'
                        );
                        // Dodaj załamania (bez pierwszego i ostatniego)
                        for (let i = 1; i < breaks.length - 1; i++) {
                            newRow.push(breaks[i]);
                        }
                    }
                }
                this.gridPoints[0] = newRow;
            }
        }
    }
    
    /**
     * Dodanie distortion do podstawowych punktów (tylko te bez distortion)
     */
    addDistortionToPoints() {
        if (this.config.verticalRandomness === 0) {
            return; // Brak distortion
        }
        
        const rows = this.gridPoints.length;
        if (rows === 0) return;
        
        const baseCols = this.config.linesCount + 1;
        
        for (let row = 0; row < rows; row++) {
            const cols = this.gridPoints[row] ? this.gridPoints[row].length : 0;
            // Tylko podstawowe kolumny (przed załamaniami)
            for (let col = 0; col < Math.min(cols, baseCols); col++) {
                const point = this.gridPoints[row][col];
                if (point && point.x !== undefined && !point._hasDistortion) {
                    // Dodaj distortion tylko jeśli punkt nie ma jeszcze distortion
                    const noise = simpleNoise(point.x * 0.1, point.z * 0.1);
                    const randomOffset = (noise - 0.5) * 2 * this.config.verticalRandomness;
                    point.y = point.y + randomOffset;
                    point._hasDistortion = true; // Oznacz że punkt ma już distortion
                }
            }
        }
    }
    
    /**
     * Dodanie załamań do całej siatki punktów - rozszerza gridPoints o dodatkowe punkty
     * Załamania są deterministyczne (używają noise) więc pozostają stałe
     * Używane tylko podczas inicjalizacji
     */
    addBreaksToGridPoints() {
        const breaksConfig = this.config.lineBreaks;
        if (!breaksConfig || (!breaksConfig.horizontal && !breaksConfig.vertical)) {
            return; // Brak załamań
        }
        
        const rows = this.gridPoints.length;
        if (rows === 0) return;
        
        const cols = this.gridPoints[0] ? this.gridPoints[0].length : 0;
        if (cols === 0) return;
        
        // Nowa siatka z załamaniami
        const newGridPoints = [];
        
        for (let row = 0; row < rows; row++) {
            const newRow = [];
            
            for (let col = 0; col < cols; col++) {
                const point = this.gridPoints[row][col];
                
                // Dodaj podstawowy punkt (kopiuj również flagę _hasDistortion)
                newRow.push({ x: point.x, y: point.y, z: point.z, _hasDistortion: point._hasDistortion });
                
                // Jeśli to nie ostatnia kolumna i załamania poziome są włączone
                if (col < cols - 1 && breaksConfig.horizontal && breaksConfig.count > 0) {
                    const nextPoint = this.gridPoints[row][col + 1];
                    const breaks = this.generateBreaksBetweenPoints(
                        point, 
                        nextPoint, 
                        breaksConfig,
                        'horizontal'
                    );
                    // Dodaj załamania (bez pierwszego i ostatniego - już dodane)
                    for (let i = 1; i < breaks.length - 1; i++) {
                        newRow.push(breaks[i]);
                    }
                }
            }
            
            newGridPoints.push(newRow);
        }
        
        // Teraz dodaj załamania pionowe - przejdź przez kolumny
        if (breaksConfig.vertical && breaksConfig.count > 0) {
            const finalGridPoints = [];
            const newRows = newGridPoints.length;
            const newCols = newGridPoints[0] ? newGridPoints[0].length : 0;
            
            // Dla każdej kolumny
            for (let col = 0; col < newCols; col++) {
                let colIndex = 0;
                
                for (let row = 0; row < newRows; row++) {
                    if (!finalGridPoints[row]) {
                        finalGridPoints[row] = [];
                    }
                    
                    const point = newGridPoints[row][col];
                    finalGridPoints[row][colIndex] = { x: point.x, y: point.y, z: point.z };
                    
                    // Jeśli to nie ostatni rząd, dodaj załamania pionowe
                    if (row < newRows - 1 && breaksConfig.vertical) {
                        const nextPoint = newGridPoints[row + 1][col];
                        const breaks = this.generateBreaksBetweenPoints(
                            point,
                            nextPoint,
                            breaksConfig,
                            'vertical'
                        );
                        
                        // Dodaj załamania jako nowe rzędy
                        for (let i = 1; i < breaks.length - 1; i++) {
                            colIndex++;
                            const breakRow = row + i;
                            if (!finalGridPoints[breakRow]) {
                                finalGridPoints[breakRow] = [];
                            }
                            // Wypełnij poprzednie kolumny jeśli potrzeba
                            while (finalGridPoints[breakRow].length < colIndex) {
                                finalGridPoints[breakRow].push(null);
                            }
                            finalGridPoints[breakRow][colIndex] = breaks[i];
                        }
                    }
                    
                    colIndex++;
                }
            }
            
            // Wypełnij puste miejsca (null) interpolacją
            this.fillEmptyGridCells(finalGridPoints);
            this.gridPoints = finalGridPoints;
        } else {
            this.gridPoints = newGridPoints;
        }
    }
    
    /**
     * Generowanie załamań między dwoma punktami (deterministyczne)
     */
    generateBreaksBetweenPoints(p1, p2, breaksConfig, direction) {
        if (!breaksConfig || breaksConfig.count <= 0) {
            return [p1, p2];
        }
        
        const points = [p1];
        const count = breaksConfig.count;
        const strength = breaksConfig.strength !== undefined ? breaksConfig.strength : 1.0;
        const position = breaksConfig.position || 'random';
        
        // Wygeneruj wartości t dla wszystkich załamań
        const tValues = [];
        if (position === 'even') {
            // Równomiernie rozłożone - dzieli linię na count+1 równych części
            for (let i = 1; i <= count; i++) {
                tValues.push(i / (count + 1));
            }
        } else { // random - ale deterministyczny noise
            // Wygeneruj wszystkie wartości t
            for (let i = 1; i <= count; i++) {
                const noisePos = simpleNoise(p1.x * 0.1 + i, p1.z * 0.1 + i);
                tValues.push(noisePos * 0.8 + 0.1); // Między 10% a 90%
            }
            // Posortuj wartości t, żeby punkty były w kolejności od p1 do p2
            tValues.sort((a, b) => a - b);
        }
        
        // Generuj punkty załamań w kolejności od p1 do p2
        for (const t of tValues) {
            // Interpolacja pozycji wzdłuż linii 3D między punktami (które już mają distortion)
            // Używamy liniowej interpolacji dla X, Y, Z
            const x = p1.x + (p2.x - p1.x) * t;
            const z = p1.z + (p2.z - p1.z) * t;
            // Y interpolowane wzdłuż linii między punktami z distortion
            const baseY = p1.y + (p2.y - p1.y) * t;
            
            // Dodaj wariację załamań tylko jeśli strength > 0
            let y = baseY;
            if (strength > 0) {
                const breakNoise = simpleNoise(x * 0.15, z * 0.15);
                const yVariation = (breakNoise - 0.5) * 2 * strength;
                y = baseY + yVariation;
            }
            
            points.push({ x, y, z });
        }
        
        points.push(p2);
        return points;
    }
    
    /**
     * Wypełnienie pustych komórek w siatce (null) interpolacją
     */
    fillEmptyGridCells(grid) {
        const rows = grid.length;
        if (rows === 0) return;
        
        const cols = grid[0] ? grid[0].length : 0;
        
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                if (grid[row][col] === null || grid[row][col] === undefined) {
                    // Znajdź najbliższe niepuste punkty
                    let leftPoint = null;
                    let rightPoint = null;
                    let topPoint = null;
                    let bottomPoint = null;
                    
                    // Szukaj w lewo
                    for (let c = col - 1; c >= 0; c--) {
                        if (grid[row][c] !== null && grid[row][c] !== undefined) {
                            leftPoint = grid[row][c];
                            break;
                        }
                    }
                    
                    // Szukaj w prawo
                    for (let c = col + 1; c < cols; c++) {
                        if (grid[row][c] !== null && grid[row][c] !== undefined) {
                            rightPoint = grid[row][c];
                            break;
                        }
                    }
                    
                    // Szukaj w górę
                    for (let r = row - 1; r >= 0; r--) {
                        if (grid[r][col] !== null && grid[r][col] !== undefined) {
                            topPoint = grid[r][col];
                            break;
                        }
                    }
                    
                    // Szukaj w dół
                    for (let r = row + 1; r < rows; r++) {
                        if (grid[r][col] !== null && grid[r][col] !== undefined) {
                            bottomPoint = grid[r][col];
                            break;
                        }
                    }
                    
                    // Interpoluj
                    let x = 0, y = 0, z = 0;
                    let count = 0;
                    
                    if (leftPoint && rightPoint) {
                        x = (leftPoint.x + rightPoint.x) / 2;
                        z = (leftPoint.z + rightPoint.z) / 2;
                        y = (leftPoint.y + rightPoint.y) / 2;
                        count = 1;
                    } else if (leftPoint) {
                        x = leftPoint.x;
                        y = leftPoint.y;
                        z = leftPoint.z;
                        count = 1;
                    } else if (rightPoint) {
                        x = rightPoint.x;
                        y = rightPoint.y;
                        z = rightPoint.z;
                        count = 1;
                    }
                    
                    if (topPoint && bottomPoint && count === 0) {
                        x = (topPoint.x + bottomPoint.x) / 2;
                        z = (topPoint.z + bottomPoint.z) / 2;
                        y = (topPoint.y + bottomPoint.y) / 2;
                        count = 1;
                    } else if (topPoint && count === 0) {
                        x = topPoint.x;
                        y = topPoint.y;
                        z = topPoint.z;
                        count = 1;
                    } else if (bottomPoint && count === 0) {
                        x = bottomPoint.x;
                        y = bottomPoint.y;
                        z = bottomPoint.z;
                        count = 1;
                    }
                    
                    if (count > 0) {
                        grid[row][col] = { x, y, z };
                    }
                }
            }
        }
    }
    
    /**
     * Zbierz wszystkie segmenty linii (pary punktów) z aktualnej siatki.
     * Linie poziome: między sąsiednimi kolumnami w rzędzie.
     * Linie pionowe: między sąsiednimi rzędami w kolumnie (z pominięciem kolumn załamań jeśli trzeba).
     * @returns {Array<{p1: {x,y,z}, p2: {x,y,z}}>}
     */
    collectLineSegments() {
        const rows = this.gridPoints.length;
        if (rows === 0) return [];

        const cols = this.gridPoints[0] ? this.gridPoints[0].length : 0;
        if (cols === 0) return [];

        const segments = [];
        const breaksConfig = this.config.lineBreaks;
        const skipVerticalForBreaks = breaksConfig && breaksConfig.skipVerticalForBreaks;
        const baseCols = this.config.linesCount + 1;
        const breakCount = (breaksConfig && breaksConfig.count) || 0;

        const isVerticalColumnAllowed = (col) => {
            if (!skipVerticalForBreaks || cols <= baseCols || breakCount <= 0) {
                return true;
            }
            const isBaseColumn = (col % (breakCount + 1)) === 0;
            const baseColIndex = Math.floor(col / (breakCount + 1));
            return isBaseColumn && baseColIndex < baseCols;
        };

        // Linie poziome
        for (let row = 0; row < rows; row++) {
            const rowPoints = this.gridPoints[row];
            if (!rowPoints) continue;
            for (let col = 0; col < rowPoints.length - 1; col++) {
                const p1 = rowPoints[col];
                const p2 = rowPoints[col + 1];
                if (p1 && p2 && p1.x !== undefined && p2.x !== undefined) {
                    segments.push({ p1, p2 });
                }
            }
        }

        // Linie pionowe
        for (let col = 0; col < cols; col++) {
            if (!isVerticalColumnAllowed(col)) continue;
            for (let row = 0; row < rows - 1; row++) {
                const p1 = this.gridPoints[row]?.[col];
                const p2 = this.gridPoints[row + 1]?.[col];
                if (p1 && p2 && p1.x !== undefined && p2.x !== undefined) {
                    segments.push({ p1, p2 });
                }
            }
        }

        return segments;
    }

    /**
     * Rebuild linii z aktualnych gridPoints - jeden InstancedMesh (grube) lub LineSegments (cienkie).
     * Wywoływane na żądanie (flaga _dirty) przy dodaniu/usunięciu segmentu.
     * Zmniejsza draw calls z setek do 1.
     */
    rebuildLines() {
        const segments = this.collectLineSegments();
        const useThickLines = this.config.useThickLines !== false;

        if (useThickLines) {
            this.rebuildInstancedCylinders(segments);
        } else {
            this.rebuildThinLines(segments);
        }
    }

    /**
     * Zwolnij aktualny mesh linii
     */
    disposeLineMesh() {
        if (!this.lineMesh) return;
        this.group.remove(this.lineMesh);
        // Geometria jednostkowa cylindra jest współdzielona - nie dysponujemy jej tutaj.
        if (this.lineMesh.isInstancedMesh) {
            this.lineMesh.dispose();
            this.lineMeshCapacity = 0;
        } else if (this.lineMesh.geometry) {
            this.lineMesh.geometry.dispose();
        }
        this.lineMesh = null;
    }

    /**
     * Grube linie: jeden InstancedMesh cylindrów jednostkowych osi Y.
     * Każdy segment = jedna instancja (skala Y = długość, rotacja = kierunek, translacja = środek).
     * InstancedMesh jest prealokowany z capacity i reuse'owany między rebuildami (mniej GC).
     */
    rebuildInstancedCylinders(segments) {
        const count = segments.length;

        // Pusta siatka - nic nie rysuj (ale zgasz istniejący mesh przez count=0).
        if (count === 0) {
            if (this.lineMesh?.isInstancedMesh) {
                this.lineMesh.count = 0;
            }
            return;
        }

        const lineWidth = Math.max(this.config.lineWidth || 1.0, 0.001);

        // Mniej segmentów obwodu dla cienkich linii (sub-pikselowe → 6 wystarcza).
        const radialSegments = lineWidth < 0.2 ? 6 : 8;

        // (Re)utwórz geometrię cylindra tylko gdy liczba segmentów obwodu się zmieniła.
        if (!this.unitCylinderGeometry || this.cylinderRadialSegments !== radialSegments) {
            if (this.unitCylinderGeometry) this.unitCylinderGeometry.dispose();
            // CylinderGeometry: oś Y, wysokość 1 (od -0.5 do 0.5), promień 1
            this.unitCylinderGeometry = new THREE.CylinderGeometry(1, 1, 1, radialSegments, 1);
            this.cylinderRadialSegments = radialSegments;
            // Geometria się zmieniła → wymuś recreate InstancedMesh.
            if (this.lineMesh?.isInstancedMesh) {
                this.disposeLineMesh();
            }
        }

        // Reuse istniejącego InstancedMesh jeśli pojemność wystarcza.
        let mesh = this.lineMesh;
        if (!mesh || !mesh.isInstancedMesh || this.lineMeshCapacity < count) {
            this.disposeLineMesh();
            // Prealokuj ze slackiem (następny multiple 16) - unika recreate przy każdym dodaniu segmentu.
            const capacity = Math.max(16, Math.ceil(count / 16) * 16);
            mesh = new THREE.InstancedMesh(this.unitCylinderGeometry, this.material, capacity);
            mesh.renderOrder = 1;
            mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
            mesh.frustumCulled = false; // Cała droga zawsze w kadrze - pomiń per-frame frustum test.
            this.group.add(mesh);
            this.lineMesh = mesh;
            this.lineMeshCapacity = capacity;
        }

        const dummy = new THREE.Object3D();
        const up = new THREE.Vector3(0, 1, 0);
        const dir = new THREE.Vector3();

        for (let i = 0; i < count; i++) {
            const { p1, p2 } = segments[i];
            dir.set(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z);
            const len = dir.length();
            if (len < 1e-6) {
                // Zerowa długość - ukryj instancję skalując do 0
                dummy.position.set(p1.x, p1.y, p1.z);
                dummy.scale.set(0, 0, 0);
                dummy.quaternion.set(0, 0, 0, 1);
            } else {
                dir.multiplyScalar(1 / len);
                dummy.position.set((p1.x + p2.x) / 2, (p1.y + p2.y) / 2, (p1.z + p2.z) / 2);
                dummy.scale.set(lineWidth, len, lineWidth);
                dummy.quaternion.setFromUnitVectors(up, dir);
            }
            dummy.updateMatrix();
            mesh.setMatrixAt(i, dummy.matrix);
        }
        mesh.count = count; // Rysuj tylko pierwsze `count` instancji.
        mesh.instanceMatrix.needsUpdate = true;
        mesh.computeBoundingSphere();
    }

    /**
     * Cienkie linie: jeden LineSegments z BufferGeometry zbudowanym z wszystkich par.
     */
    rebuildThinLines(segments) {
        this.disposeLineMesh();

        if (segments.length === 0) return;

        const positions = new Float32Array(segments.length * 6);
        for (let i = 0; i < segments.length; i++) {
            const { p1, p2 } = segments[i];
            const o = i * 6;
            positions[o] = p1.x;
            positions[o + 1] = p1.y;
            positions[o + 2] = p1.z;
            positions[o + 3] = p2.x;
            positions[o + 4] = p2.y;
            positions[o + 5] = p2.z;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        const lines = new THREE.LineSegments(geometry, this.lineMaterial);
        lines.renderOrder = 1;
        this.group.add(lines);
        this.lineMesh = lines;
    }
    
    /**
     * Oblicz boost HDR koloru linii z glowIntensity. Ujednolicony dla init i updateLineStyle.
     */
    computeBloomBoost(glowIntensity) {
        return Math.max(glowIntensity * 1.1, 2.5);
    }

    /**
     * Aktualizacja parametrów linii (glow). Wszystkie instancje współdzielą this.material.
     */
    updateLineStyle() {
        const glowIntensity = this.config.glowIntensity || 1.0;
        const baseOpacity = 0.8;
        const opacity = Math.min(baseOpacity * glowIntensity, 1.0);

        // Odśwież boost HDR koloru (zależy od glowIntensity) - reset do bazowego i pomnóż.
        this.bloomBoost = this.computeBloomBoost(glowIntensity);
        this.material.color.setHex(this.baseColor).multiplyScalar(this.bloomBoost);
        this.lineMaterial.color.setHex(this.baseColor).multiplyScalar(this.bloomBoost);

        this.material.opacity = opacity;
        this.lineMaterial.opacity = opacity;
        this.material.needsUpdate = true;
        this.lineMaterial.needsUpdate = true;
    }
    
    /**
     * Aktualizacja konfiguracji w runtime
     */
    updateConfig(newConfig) {
        if (!newConfig) return;
        
        let needsRebuild = false;

        // Aktualizuj konfigurację
        if (newConfig.linesCount !== undefined) {
            this.config.linesCount = newConfig.linesCount;
            needsRebuild = true;
        }
        if (newConfig.lineWidth !== undefined) {
            this.config.lineWidth = newConfig.lineWidth;
            needsRebuild = true;
        }
        if (newConfig.glowIntensity !== undefined) this.config.glowIntensity = newConfig.glowIntensity;
        if (newConfig.verticalRandomness !== undefined) this.config.verticalRandomness = newConfig.verticalRandomness;
        if (newConfig.blockNewSegments !== undefined) this.config.blockNewSegments = newConfig.blockNewSegments;
        if (newConfig.lineBreaks) {
            Object.assign(this.config.lineBreaks, newConfig.lineBreaks);
            needsRebuild = true;
        }

        // Aktualizuj style linii (glow)
        this.updateLineStyle();

        // Jeśli zmieniono parametry wymagające przebudowy, oznacz siatkę jako brudną
        if (needsRebuild) {
            this._dirty = true;
        }
    }
    
    /**
     * Aktualizacja drogi - zarządza segmentami
     */
    update(cameraZ) {
        // cameraZ to pozycja Z kamery w przestrzeni świata
        // Dodaj nowe segmenty z przodu
        this.addRoadSegment(cameraZ);
        
        // Usuń stare segmenty z tyłu (za kamerą)
        this.removeRoadSegment(cameraZ);

        // Rebuild linii tylko gdy siatka się zmieniła (dodano/usunięto segment).
        // Jeden InstancedMesh zamiast setek meshy - 1 draw call.
        if (this._dirty) {
            this.rebuildLines();
            this._dirty = false;
        }
    }
    
    /**
     * Dodanie nowego segmentu drogi z przodu (na horyzoncie)
     */
    addRoadSegment(cameraZ) {
        // Sprawdź czy dodawanie nowych segmentów jest zablokowane
        if (this.config.blockNewSegments) {
            return; // Nie dodawaj nowych segmentów
        }
        
        const linesCount = this.config.linesCount;
        const segmentSpacing = this.config.segmentSpacing;
        const roadWidth = this.config.roadWidth;
        
        // Sprawdź czy potrzebujemy nowych punktów z przodu
        let needsNewSegment = false;
        let newZ = 0;
        
        if (this.gridPoints.length === 0) {
            // Jeśli nie ma punktów, zacznij od startZ
            needsNewSegment = true;
            newZ = this.config.startZ;
        } else {
            // Sprawdź pierwszy rząd (najdalszy od kamery - na horyzoncie)
            const firstRow = this.gridPoints[0];
            if (firstRow && firstRow.length > 0) {
                const firstZ = firstRow[0].z;
                // Odległość między kamerą a najdalszym segmentem
                const distanceToHorizon = cameraZ - firstZ;
                
                // Jeśli kamera zbliża się do horyzontu (odległość < 100), dodaj nowy segment dalej
                // Dodajemy segment gdy odległość jest mniejsza niż buffer (100 jednostek)
                // WAŻNE: Sprawdź czy już nie dodaliśmy segmentu dla tego Z (aby uniknąć wielokrotnego dodawania)
                // Dodaj nowy segment gdy odległość do horyzontu jest mniejsza niż 150
                if (distanceToHorizon < 150) {
                    // Sprawdź czy ostatnio dodany segment nie jest już na tej pozycji Z
                    const lastAddedZ = this.lastAddedSegmentZ || Infinity;
                    const proposedZ = firstZ - segmentSpacing;
                    
                    // Dodaj segment tylko jeśli nie dodaliśmy już segmentu dla tego Z
                    if (Math.abs(proposedZ - lastAddedZ) > 0.1) {
                        needsNewSegment = true;
                        // Nowy segment jeszcze dalej (mniejsza wartość Z = dalej w kierunku ujemnych Z)
                        newZ = proposedZ;
                        this.lastAddedSegmentZ = newZ; // Zapamiętaj ostatnio dodany Z
                    }
                }
            } else {
                needsNewSegment = true;
                newZ = this.config.startZ;
            }
        }
        
        if (needsNewSegment) {
            // Dodaj nowy rząd punktów z przodu (na horyzoncie)
            const newRow = [];
            const width = roadWidth; // Stała szerokość
            
            for (let col = 0; col <= linesCount; col++) {
                // Równomierny rozkład punktów w linii poziomej
                const u = col / linesCount;
                const x = (u - 0.5) * 2 * width;
                
                // Y bez distortion - distortion będzie dodany po załamaniach
                const y = -5;
                
                newRow.push({ x, y, z: newZ });
            }
            
            // Dodaj nowy rząd na początku tablicy (najdalszy od kamery)
            this.gridPoints.unshift(newRow);
            
            // Ogranicz liczbę rzędów
            while (this.gridPoints.length > this.maxRows) {
                this.gridPoints.pop();
            }
            
            // WAŻNE: Najpierw dodaj distortion i załamania do nowego segmentu
            // A dopiero potem odbuduj geometrie - to zapewnia, że geometrie są tworzone
            // z punktów, które mają już wszystkie modyfikacje (distortion + breaks)
            this.addDistortionToNewSegment();
            this.addBreaksToNewSegment();
            
            // NIE dodajemy distortion do punktów załamań - one już mają właściwe Y z interpolacji!

            // Oznacz siatkę jako brudną - rebuild InstancedMesh w update()
            this._dirty = true;
            
            // Aktualizuj płaszczyznę, żeby pokrywała wszystkie segmenty (w tym nowe)
            this.updateRoadPlane();
        }
    }
    
    /**
     * Usunięcie segmentów drogi za kamerą
     */
    removeRoadSegment(cameraZ) {
        // cameraZ to pozycja Z kamery w przestrzeni świata
        // Usuń segmenty, które są za kamerą
        
        let removed = false;
        
        // Sprawdzaj od końca (najbliższe kamerze)
        while (this.gridPoints.length > 0) {
            const lastRow = this.gridPoints[this.gridPoints.length - 1];
            if (lastRow && lastRow.length > 0) {
                const pointZ = lastRow[0].z;
                
                // Usuń jeśli jest za kamerą (z > cameraZ) lub zbyt daleko za kamerą
                // Usuń segment gdy jest za kamerą (cameraZ) - wcześniejsze usuwanie
                if (pointZ > cameraZ) {
                    this.gridPoints.pop();
                    removed = true;
                } else {
                    break;
                }
            } else {
                break;
            }
        }
        
        if (removed) {
            // Linie są rebuildowane z całej siatki w update() - wystarczy flaga _dirty.
            this._dirty = true;
        }
    }
    
    /**
     * Pobierz statystyki - liczba elementów na ekranie
     */
    getStats() {
        let lines = 0;
        if (this.lineMesh) {
            if (this.lineMesh.isInstancedMesh) {
                lines = this.lineMesh.count;
            } else if (this.lineMesh.geometry?.attributes?.position) {
                lines = this.lineMesh.geometry.attributes.position.count / 2;
            }
        }
        const stats = {
            segments: this.gridPoints.length,
            lines,
            points: this.gridPoints.reduce((sum, row) => sum + (row ? row.length : 0), 0)
        };
        return stats;
    }
    
    /**
     * Loguj statystyki do konsoli
     */
    logStats() {
        const stats = this.getStats();
        console.log(`[Road Stats] Segments: ${stats.segments}, Lines: ${stats.lines}, Points: ${stats.points}`);
        return stats;
    }
    
    /**
     * Cleanup resources
     */
    dispose() {
        // Usuń mesh linii (InstancedMesh lub LineSegments)
        this.disposeLineMesh();

        // Usuń współdzieloną geometrię jednostkowego cylindra
        if (this.unitCylinderGeometry) {
            this.unitCylinderGeometry.dispose();
            this.unitCylinderGeometry = null;
        }
        
        // Usuń płaszczyznę drogi
        if (this.roadPlane) {
            if (this.roadPlane.geometry) {
                this.roadPlane.geometry.dispose();
            }
            if (this.roadPlane.material) {
                this.roadPlane.material.dispose();
            }
            this.group.remove(this.roadPlane);
        }
        
        // Usuń materiały
        if (this.material) {
            this.material.dispose();
        }
        if (this.lineMaterial) {
            this.lineMaterial.dispose();
        }
        
        this.gridPoints = [];
    }
}
